import {
  type OfferCargoProcess,
  calculateDeliveryProposalOfferCargoStatus,
  calculateOfferCargoProcessStatus,
} from "@/features/offer-cargo-process-status";
import { assertNever } from "@/shared/lib/assertNever";
import { Box, Stack, Typography } from "@mui/material";
import type React from "react";
import { BaseIcon } from "../../shared/ui/BaseIcons";

const calculateTaskCardContents = (
  props: OfferCargoTaskCardProps,
): {
  message: string;
  severity: "info" | "error";
  onClick?: () => void;
} | null => {
  const { viewer, offerCargoProcess } = props;
  const offerCargoProcessStatus = calculateOfferCargoProcessStatus({
    process: offerCargoProcess,
  });
  switch (viewer) {
    case "shipper": {
      const {
        onShowDeliveryProposalListClick,
        onShowDeliveryRequestUploaderClick,
      } = props;
      switch (offerCargoProcessStatus) {
        case "no_valid_delivery_proposals":
        case "reception_suspended":
          return null;
        case "has_ongoing_proposal_with_no_contract": {
          return {
            message: "配送提案を確認しましょう",
            severity: "info",
            onClick: onShowDeliveryProposalListClick,
          };
        }
        case "has_contracts_all_rejected": {
          return {
            message: "契約内容が見送られました",
            severity: "error",
          };
        }
        case "has_contract_pending":
        case "proposal_reception_deadline_expired":
          return null;
        case "has_contract_concluded": {
          const hasAttachedDeliveryRequestFiles =
            offerCargoProcess.contractOfferCargos.find(
              (contractOfferCargo) =>
                contractOfferCargo.stage === "contract_concluded" &&
                contractOfferCargo.attachedDeliveryRequestFiles &&
                contractOfferCargo.attachedDeliveryRequestFiles.length > 0,
            );
          return !hasAttachedDeliveryRequestFiles
            ? {
                message:
                  "積日の前日16時までに配送依頼書をアップロードまたは車主へ送付してください",
                severity: "info",
                onClick: onShowDeliveryRequestUploaderClick,
              }
            : null;
        }
        default:
          assertNever(offerCargoProcessStatus);
          throw new Error(`unknown status: ${offerCargoProcessStatus}`);
      }
    }
    case "carrier": {
      const { deliveryProposalOfferCargoId, onShowDeliveryRequestFilesClick } =
        props;
      if (!deliveryProposalOfferCargoId) {
        // まだ配送提案していない場合タスクはない
        return null;
      }
      const deliveryProposalOfferCargoStatus =
        calculateDeliveryProposalOfferCargoStatus({
          deliveryProposalOfferCargoId,
          process: offerCargoProcess,
        });
      switch (deliveryProposalOfferCargoStatus) {
        case "no_valid_contracts":
          return null;
        case "delivery_proposal_rejected":
          switch (offerCargoProcessStatus) {
            case "no_valid_delivery_proposals":
            case "reception_suspended":
              return null;
            case "has_ongoing_proposal_with_no_contract":
            case "has_contracts_all_rejected":
            case "has_contract_pending": {
              return {
                message: "提案内容が見送られました",
                severity: "error",
              };
            }
            case "proposal_reception_deadline_expired":
            case "has_contract_concluded":
              return null;
            default:
              assertNever(offerCargoProcessStatus);
              throw new Error(`unknown status: ${offerCargoProcessStatus}`);
          }
        case "has_contracts_all_rejected":
          return null;
        case "has_contract_pending": {
          return {
            message: "荷主からの契約内容を確認し合意しましょう",
            severity: "info",
          };
        }
        case "delivery_proposal_response_deadline_expired":
          return null;
        case "has_contract_concluded": {
          const hasAttachedDeliveryRequestFiles =
            offerCargoProcess.contractOfferCargos.find(
              (contractOfferCargo) =>
                contractOfferCargo.stage === "contract_concluded" &&
                contractOfferCargo.attachedDeliveryRequestFiles &&
                contractOfferCargo.attachedDeliveryRequestFiles.length > 0,
            );
          return hasAttachedDeliveryRequestFiles
            ? {
                message:
                  "荷主から配送依頼書が届いています。ダウンロードして確認してください。",
                severity: "info",
                onClick: onShowDeliveryRequestFilesClick,
              }
            : null;
        }
        default:
          assertNever(deliveryProposalOfferCargoStatus);
          throw new Error(
            `unknown status: ${deliveryProposalOfferCargoStatus}`,
          );
      }
    }
    default:
      assertNever(viewer);
      throw new Error(`unknown viewer: ${viewer}`);
  }
};

export type OfferCargoTaskCardProps = {
  offerCargoProcess: OfferCargoProcess;
} & (
  | {
      viewer: "shipper";
      onShowDeliveryProposalListClick: () => void;
      onShowDeliveryRequestUploaderClick: () => void;
    }
  | {
      viewer: "carrier";
      deliveryProposalOfferCargoId: number | undefined;
      onShowDeliveryRequestFilesClick: () => void;
    }
);

export const OfferCargoTaskCard: React.FC<OfferCargoTaskCardProps> = (
  props,
) => {
  const contents = calculateTaskCardContents(props);
  return (
    <Box
      maxWidth={{ xs: "100%", sm: "628px" }}
      borderRadius="16px"
      boxShadow="0px 1px 10px 0px #0000001A"
    >
      <Stack spacing={1} p="20px 24px">
        <Typography
          variant="body1"
          fontWeight={700}
          pl={1}
          borderLeft={"4px solid"}
          borderColor="primary.400"
        >
          現在のタスク：
        </Typography>
        {contents ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor={contents.severity === "error" ? "#FEE7E2" : "#E1F5EF"}
            px={2}
            py={1.5}
            borderRadius={4}
            sx={{ cursor: contents?.onClick ? "pointer" : "initial" }}
            onClick={contents?.onClick}
          >
            <Typography
              variant="body1"
              color={contents.severity === "error" ? "#EA583B" : "primary.400"}
              fontWeight={400}
              pr={2}
            >
              {contents.message}
            </Typography>
            {contents?.onClick && (
              <BaseIcon
                name="chevronRight"
                sx={{
                  color:
                    contents.severity === "error" ? "#EA583B" : "primary.300",
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor:
                    contents.severity === "error" ? "#EA583B" : "primary.300",
                  borderRadius: "50%",
                }}
              />
            )}
          </Box>
        ) : (
          <Typography fontWeight={400} color="grey.400">
            タスクはありません
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
